import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react'

import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { ethers } from 'ethers'
import { fromBech32, toBech32, normalizeBech32 } from '@cosmjs/encoding'
import { InputBox } from '../boxes'
import {
  AddressSwapContext, AddressSwapState, AddressType, DirectionType,
} from '../../contexts'

interface AddressTypeData {
  placeholder: string;
  cleanValue(value?: string): string | undefined;
  convert(value: string, toType: AddressType): string;
}

const typeData: Record<AddressType, AddressTypeData> = {
  [AddressType.STOS]: {
    placeholder: 'st1... address',
    cleanValue(value?: string) {
      if (!value) return undefined
      if (value.startsWith('st')) {
        try {
          const bAddr = fromBech32(value)
          ethers.utils.hexlify(bAddr.data)
          return normalizeBech32(value)
        } catch (e) {
          return undefined
        }
      }
      return undefined
    },
    convert(value: string, toType: AddressType): string {
      if (toType === AddressType.ETH) {
        return ethers.utils.getAddress(ethers.utils.hexlify(fromBech32(value).data))
      }
      throw Error('Wrong address type')
    },
  },
  [AddressType.ETH]: {
    placeholder: '0x... address',
    cleanValue(value?: string) {
      if (!value || !ethers.utils.isAddress(value)) return undefined

      return ethers.utils.getAddress(value)
    },
    convert(value: string, toType: AddressType): string {
      if (toType === AddressType.STOS) {
        return toBech32('st', ethers.utils.arrayify(value))
      }
      throw Error('Wrong address type')
    },
  },
}

interface TextFieldWithCopy {
  type: AddressType
  address: string
  placeholder: string
  error: string
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
}

const TextFieldWithCopy = ({
  type, address, placeholder, error, onInputChange, disabled,
}: TextFieldWithCopy): JSX.Element => {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return undefined

    const id = setTimeout(() => setCopied(false), 500)
    return () => {
      clearTimeout(id)
    }
  }, [copied])

  const { startIcon } = useMemo(() => ((copied) ? {
    startIcon: <CheckCircleOutlineIcon sx={{ color: 'green' }} />,
  } : {
    startIcon: <ContentCopyIcon />,
  }), [copied])

  if (disabled && address) {
    return (
      <CopyToClipboard text={address} onCopy={() => setCopied(true)}>
        <TextField
          sx={{
            width: 450,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'black',
            },
          }}
          placeholder={placeholder}
          InputProps={{
            sx: {
              borderRadius: 0,
              fontSize: 16,
            },
            disableUnderline: true,
            startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment>,
          }}
          error={error.length !== 0}
          helperText={error}
          onChange={onInputChange}
          variant="standard"
          autoComplete="off"
          autoCorrect="off"
          InputLabelProps={{ shrink: true }}
          label={`${type} based address`}
          color="secondary"
          type="text"
          disabled={disabled}
          value={(disabled) ? (address) || '-' : address}
        />
      </CopyToClipboard>
    )
  }

  return (
    <TextField
      sx={{
        width: 450,
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: 'black',
        },
      }}
      placeholder={placeholder}
      InputProps={{
        sx: {
          borderRadius: 0,
          fontSize: 16,
        },
        disableUnderline: true,
        startAdornment: <InputAdornment position="start"><AccountBalanceWalletIcon /></InputAdornment>,
      }}
      error={error.length !== 0}
      helperText={error}
      onChange={onInputChange}
      variant="standard"
      autoComplete="off"
      autoCorrect="off"
      InputLabelProps={{ shrink: true }}
      label={`${type} based address`}
      color="secondary"
      type="text"
      disabled={disabled}
      value={(disabled) ? (address) || '-' : address}
    />
  )
}

interface AddressInputProps {
  direction: DirectionType
  disabled?: boolean
}

const AddressInput = ({
  direction, disabled,
}: AddressInputProps): JSX.Element | null => {
  const [error, setError] = useState('')
  const [address, setAddress] = useState('')

  const reverseDirection = (direction === DirectionType.INPUT) ? DirectionType.OUTPUT : DirectionType.INPUT
  const { state, setState } = useContext(AddressSwapContext)

  const { type, value: result } = state[direction]
  const { type: reverseType } = state[reverseDirection]

  const tdata = typeData[type]
  const { placeholder, cleanValue, convert } = tdata

  useEffect(() => {
    if (direction !== DirectionType.INPUT) return

    console.log('result', result)
    console.log('address', address)

    if (result) {
      const reverseValue = convert(result, reverseType)
      console.log('reverseValue', reverseValue)
      setState((st) => ({
        ...st,
        [reverseDirection]: {
          ...st[reverseDirection],
          value: reverseValue,
        },
      }))
      if (result !== address) {
        setAddress(result)
      }
    }
  }, [result, address, direction, reverseDirection, reverseType, setState, convert])

  useEffect(() => {
    if (direction !== DirectionType.OUTPUT) return

    setAddress(result || '')
  }, [result, direction])

  const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target

    value = value.trim()

    const cleanedValue = cleanValue(value)
    if (cleanedValue) {
      setAddress(cleanedValue)
      setState((st) => ({
        ...st,
        [direction]: {
          ...st[direction],
          value: cleanedValue,
        },
      }))
    } else {
      setAddress(value)
      setState((st) => ({
        ...st,
        [direction]: {
          ...st[direction],
          value: undefined,
        },
        [reverseDirection]: {
          ...st[reverseDirection],
          value: undefined,
        },
      }))
    }
  }, [direction, setState, cleanValue, reverseDirection])

  return (
    <InputBox sx={{
      padding: 2,
      paddingLeft: 3,
      paddingRight: 3,
      borderRadius: 2,
    }}
    >
      <Grid container>
        <Grid item xs={12} display="flex">
          <TextFieldWithCopy
            placeholder={placeholder}
            error={error}
            onInputChange={onInputChange}
            type={type}
            disabled={disabled}
            address={address}
          />
        </Grid>
      </Grid>
    </InputBox>
  )
}

export default AddressInput
